/**
 * Application shell.
 *
 * Mobile-first: the phone gets a bottom tab bar with safe-area padding, the desktop gets
 * a labelled sidebar. Both render from the same route table, so a new page appears in
 * both without being added twice.
 *
 * The predecessor had a 72px icon rail on desktop and a hamburger drawer on mobile with
 * the theme switcher hidden on phones. Bottom tabs are the right pattern here because
 * thumb reach matters more than screen area on the device where this app is mostly read
 * rather than driven.
 */

import { type ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  LayoutGrid,
  Moon,
  Search,
  Settings,
  Sparkles,
  Sun,
} from "lucide-react";

import { useHealth, useIsOwner } from "@/api/hooks";
import { classNames } from "@/core/format";
import { useThemeStore } from "@/stores/theme";

interface Route {
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  /** Shown in the bottom tab bar. Five is the most that fits legibly on a phone. */
  primary: boolean;
  /** Hidden from members. The dashboard reports lake and sync state, and the server
   *  answers its endpoints with 403 for anyone but the owner. The data console lives
   *  under Settings → 数据, where the tab itself is owner-only. */
  ownerOnly?: boolean;
}

const ROUTES: Route[] = [
  { to: "/", label: "看板", icon: LayoutGrid, primary: true, ownerOnly: true },
  { to: "/screen", label: "选股", icon: Search, primary: true },
  { to: "/strategies", label: "策略", icon: BarChart3, primary: true },
  { to: "/reports", label: "报告", icon: Activity, primary: true },
  { to: "/assistant", label: "助手", icon: Sparkles, primary: false },
  { to: "/settings", label: "设置", icon: Settings, primary: true },
];

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { theme, cycle } = useThemeStore();
  const { data: health } = useHealth();
  const isOwner = useIsOwner() === true;
  const [showBanner, setShowBanner] = useState(true);

  // A route change on mobile should return to the top; a preserved scroll position from
  // a different page reads as a rendering bug.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // The degraded banner lists operational problems -- a missing calendar, an unset data
  // token -- that only the owner can fix. Shown to a member it is noise they cannot act on.
  const degraded = isOwner && health?.status === "degraded";
  const routes = ROUTES.filter((route) => isOwner || !route.ownerOnly);
  const primary = routes.filter((route) => route.primary);

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden w-56 shrink-0 border-r border-surface-border bg-surface-raised md:flex md:flex-col">
        <div className="flex items-center gap-2 px-4 py-5">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-brand text-sm font-bold text-white">
            Q
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold">QuantLab</div>
            <div className="text-[11px] text-ink-faint">v{health?.version ?? "2.0.0"}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-2">
          {routes.map((route) => (
            <SidebarLink key={route.to} route={route} />
          ))}
        </nav>
        <button type="button" onClick={cycle} className="btn-quiet mx-2 mb-3 justify-start">
          {theme === "light" ? <Sun size={16} /> : <Moon size={16} />}
          <span>{{ dark: "深色", light: "浅色", glass: "玻璃" }[theme]}</span>
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile header */}
        <header className="safe-top sticky top-0 z-30 flex items-center justify-between border-b border-surface-border bg-surface-raised px-4 pb-3 md:hidden">
          <div className="flex items-center gap-2">
            <div className="grid h-7 w-7 place-items-center rounded-md bg-brand text-xs font-bold text-white">
              Q
            </div>
            <span className="text-sm font-semibold">QuantLab</span>
          </div>
          <div className="flex items-center gap-1">
            <NavLink to="/assistant" className="btn-quiet min-h-0 px-2 py-1.5" aria-label="AI 助手">
              <Sparkles size={18} />
            </NavLink>
            <NavLink to="/signals" className="btn-quiet min-h-0 px-2 py-1.5" aria-label="信号推送">
              <Bell size={18} />
            </NavLink>
            {/* The theme switcher is reachable on mobile. The predecessor hid it, which
                left phone users stuck on whatever theme they first loaded. */}
            <button
              type="button"
              onClick={cycle}
              className="btn-quiet min-h-0 px-2 py-1.5"
              aria-label="切换主题"
            >
              {theme === "light" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>

        {degraded && showBanner && (
          <div className="alarm m-3 md:m-4">
            <div className="flex-1">
              <div className="font-medium">服务处于降级状态</div>
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                {health?.warnings.slice(0, 3).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              className="text-xs underline"
            >
              知道了
            </button>
          </div>
        )}

        <main className="min-w-0 flex-1 px-3 pb-24 pt-3 md:px-6 md:pb-8 md:pt-6">
          {children}
        </main>
      </div>

      {/* Mobile bottom tabs */}
      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex border-t border-surface-border bg-surface-raised md:hidden">
        {primary.map((route) => (
          <TabLink key={route.to} route={route} />
        ))}
      </nav>
    </div>
  );
}

function SidebarLink({ route }: { route: Route }) {
  const Icon = route.icon;
  return (
    <NavLink
      to={route.to}
      end={route.to === "/"}
      className={({ isActive }) =>
        classNames(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
          isActive
            ? "bg-brand-soft font-medium text-brand"
            : "text-ink-muted hover:bg-surface-overlay hover:text-ink",
        )
      }
    >
      <Icon size={18} />
      <span>{route.label}</span>
    </NavLink>
  );
}

function TabLink({ route }: { route: Route }) {
  const Icon = route.icon;
  return (
    <NavLink
      to={route.to}
      end={route.to === "/"}
      className={({ isActive }) =>
        classNames(
          // min-h-14 keeps the tap target well above the 44px floor once safe-area
          // padding is added underneath.
          "flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px]",
          isActive ? "text-brand" : "text-ink-faint",
        )
      }
    >
      <Icon size={20} />
      <span>{route.label}</span>
    </NavLink>
  );
}
